import { Alert, Input, InputNumber, Typography } from 'antd';
import ToggleRow from '../common/ToggleRow';
import { NUMBER_LIMITS } from '../../utils/elementValidation';
import { formatNumber } from '../../utils/elementFormat/number';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';

/**
 * "Number this automatically" — the card-definition sub-editor for a
 * SERVER-ALLOCATED sequence (docs/SCHEMA.md § "Sequence numbers").
 *
 * Work order numbers, invoice numbers, ticket numbers. Before this the
 * platform issued none: a number had to be typed, or minted by a script, and
 * a record an automation opened by itself came out blank.
 *
 * WHAT IS STORED IS THE NUMBER. 1000, not "WO-001000". The prefix and the
 * padding are the element's ordinary display options, applied when the value
 * is rendered — which is why the list still sorts in number order, why
 * "more than 1000" still means more than 1000, and why changing the prefix
 * later costs nothing. That also settles where the two keys live: `prefix`
 * and `leadingZeros` sit beside the sequence, not inside it, because they
 * are how ANY number on this element is spelled.
 *
 * The four things an author chooses, and one they have to be told about:
 *
 *   Prefix          `options.prefix`                 (display)
 *   Leading zeros   `options.leadingZeros`           (display, 2–20)
 *   Start at        `options.sequence.start`
 *   Increment by    `options.sequence.step`
 *   At the maximum  `options.sequence.extendOnOverflow`
 *
 * THE LAST ONE IS THE ONE PEOPLE MISREAD, so the copy says it flatly rather
 * than naming the switch and hoping. Padding is a display rule that never
 * truncates, so running past the padded width is not an error and never was
 * — the number simply renders wider. The switch's real job is the OTHER
 * answer: turn it off and the platform REFUSES to create the record once the
 * width is used up, which is what you want when something downstream depends
 * on the width and not what you want otherwise.
 *
 * Writes go through the form's path-based onChange, like every other
 * sub-editor; clearing a field writes `undefined` so the key serializes away
 * instead of storing a null the API would have to read as "off".
 */

const { Text } = Typography;

const MAX_PREFIX = 16;
const MAX_STEP = 1000000;

const intOrUndefined = v => (typeof v === 'number' && Number.isInteger(v) ? v : undefined);

// The numbering switch's one line: what it does, or what holds it off.
const sequenceDescription = (blockedBy, amountSwitch) => {
	if (blockedBy) return `Not available while this element has ${blockedBy} — one field cannot have two owners.`;
	if (amountSwitch) return `Not available while ${amountSwitch} is on — a number keepr assigns is an identifier, not an amount.`;
	return 'The server assigns the next number when a record is created. Nobody types it, and nobody can change it afterwards.';
};

export default function SequenceOptions({ index, element, onChange, disabled = false }) {
	const options = element?.options || {};
	const sequence = options.sequence && typeof options.sequence === 'object' ? options.sequence : null;
	const prefix = `elements[${index}].options`;

	const set = (key, value) => onChange(`${prefix}.${key}`, value);
	const setSeq = (key, value) => onChange(`${prefix}.sequence.${key}`, value);

	// The two configurations that already own this element's value. The API
	// refuses either pairing, so the switch says why instead of letting the
	// save fail — and says it without destroying what is there.
	const blockedBy = options.drivenFrom
		? 'a calculated or driven value'
		: (options.defaultValue !== undefined && options.defaultValue !== null && options.defaultValue !== ''
			? 'a default value'
			: null);
	// PR 7: a numbered value is an identifier, never a percentage or grouped
	// (the API refuses both pairings) — a different reason, said differently.
	const amountSwitch = options.percent === true ? 'Show as a percent' : (options.thousands === true ? 'Group thousands' : null);

	const start = intOrUndefined(sequence?.start) ?? 1;
	const step = intOrUndefined(sequence?.step) ?? 1;
	const extend = sequence ? sequence.extendOnOverflow !== false : true;
	const width = intOrUndefined(options.leadingZeros);

	// What the first record will actually read. Built with the same vendored
	// formatter the API, the list, the export and the read view use, so the
	// sample cannot drift from the value.
	const sample = formatNumber(start, { prefix: options.prefix, leadingZeros: options.leadingZeros });
	const secondSample = formatNumber(start + step, { prefix: options.prefix, leadingZeros: options.leadingZeros });

	const toggle = (on) => {
		if (on) {
			// Written as one object so a half-configured sequence never
			// exists: the defaults ARE the configuration until edited.
			set('sequence', { start: 1, step: 1, extendOnOverflow: true });
		} else {
			// The display keys are left alone: they are number-format
			// options in their own right, and an author who turns numbering
			// off usually still wants "WO-" on what is already there.
			set('sequence', undefined);
		}
	};

	return (
		<div className='sequence-options'>
			<div className='micro-label card-def-element__section'>Numbering</div>

			<ToggleRow
				first
				label='Number this automatically'
				description={sequenceDescription(blockedBy, amountSwitch)}
				checked={!!sequence}
				onChange={toggle}
				disabled={disabled || (!sequence && !!(blockedBy || amountSwitch))}
				aria-label='Number this automatically'
			/>

			{sequence ? (
				<>
					<div className='number-options__row'>
						<div className='mb-2 number-options__cell'>
							<label className='d-block' htmlFor={`${prefix}.prefix`}>Prefix</label>
							<Input
								{...NO_TEXT_ASSIST}
								id={`${prefix}.prefix`}
								size='large'
								maxLength={MAX_PREFIX}
								value={typeof options.prefix === 'string' ? options.prefix : ''}
								onChange={e => set('prefix', e.target.value ? e.target.value : undefined)}
								placeholder='None'
								disabled={disabled}
								aria-label='Prefix'
							/>
							<div className='form-panel__hint'>Shown in front of the number. Not part of what is stored.</div>
						</div>
						<div className='mb-2 number-options__cell'>
							<label className='d-block' htmlFor={`${prefix}.leadingZeros`}>Leading zeros</label>
							<InputNumber
								id={`${prefix}.leadingZeros`}
								size='large'
								className='w-100'
								min={NUMBER_LIMITS.MIN_LEADING_ZEROS}
								max={NUMBER_LIMITS.MAX_LEADING_ZEROS}
								step={1}
								precision={0}
								value={width ?? null}
								onChange={v => set('leadingZeros', intOrUndefined(v))}
								placeholder='Off'
								inputMode='numeric'
								disabled={disabled}
								aria-label='Leading zeros'
							/>
							<div className='form-panel__hint'>Digits to pad to — 6 shows 42 as 000042.</div>
						</div>
					</div>

					<div className='number-options__row'>
						<div className='mb-2 number-options__cell'>
							<label className='d-block' htmlFor={`${prefix}.sequence.start`}>Start at</label>
							<InputNumber
								id={`${prefix}.sequence.start`}
								size='large'
								className='w-100'
								step={1}
								precision={0}
								value={start}
								onChange={v => setSeq('start', intOrUndefined(v) ?? 1)}
								inputMode='numeric'
								disabled={disabled}
								aria-label='Start at'
							/>
							<div className='form-panel__hint'>
								Only in force until the first record is numbered; after that the series carries on from where it is.
							</div>
						</div>
						<div className='mb-2 number-options__cell'>
							<label className='d-block' htmlFor={`${prefix}.sequence.step`}>Increment by</label>
							<InputNumber
								id={`${prefix}.sequence.step`}
								size='large'
								className='w-100'
								min={1}
								max={MAX_STEP}
								step={1}
								precision={0}
								value={step}
								onChange={v => setSeq('step', intOrUndefined(v) ?? 1)}
								inputMode='numeric'
								disabled={disabled}
								aria-label='Increment by'
							/>
							<div className='form-panel__hint'>Changing this affects the numbers still to come, never the ones already issued.</div>
						</div>
					</div>

					<ToggleRow
						label='Keep going past the padded width'
						description={width
							? `At ${width} digits the series runs out after ${formatNumber(Math.pow(10, width) - 1, { prefix: options.prefix, leadingZeros: width })}. On: the next number simply shows wider (${formatNumber(Math.pow(10, width), { prefix: options.prefix })}). Off: records STOP SAVING until you widen this.`
							: 'With no leading zeros there is no width to run out of, so this changes nothing.'}
						checked={extend}
						onChange={on => setSeq('extendOnOverflow', on ? undefined : false)}
						disabled={disabled}
						aria-label='Keep going past the padded width'
					/>

					{!extend && width ? (
						<Alert
							type='warning'
							showIcon
							className='mb-2'
							message='Records will be refused when the numbers run out'
							description={`Once ${formatNumber(Math.pow(10, width) - 1, { prefix: options.prefix, leadingZeros: width })} has been issued, saving a new record fails until someone raises the leading zeros. Choose this only when something outside keepr depends on the width.`}
						/>
					) : null}

					<Text type='secondary' className='d-block'>
						The first record will read <strong>{sample}</strong>, the next <strong>{secondSample}</strong>.
						The value stored is the plain number, so the list sorts and filters on it as a number.
					</Text>
				</>
			) : null}
		</div>
	);
}
